/**
 * SecretField — masked value display with copy and reveal controls.
 *
 * Used for password-type lobby fields so streamers don't leak
 * credentials on screen while players can still copy or reveal them.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { cn } from "../../lib/utils";

type SecretFieldProps = {
  value: string;
};

export function SecretField({ value }: SecretFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: do nothing
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "font-mono font-bold tracking-[0.1em]",
          revealed ? "text-brand-400" : "text-surface-500 tracking-widest",
        )}
      >
        {revealed ? value : "••••••••"}
      </span>

      {/* Reveal toggle */}
      <button
        type="button"
        onClick={() => setRevealed((r) => !r)}
        className="p-1 rounded text-surface-500 hover:text-surface-300 transition-colors"
        aria-label={revealed ? "Hide password" : "Reveal password"}
      >
        {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>

      {/* Copy button */}
      <button
        type="button"
        onClick={handleCopy}
        className="p-1 rounded text-surface-500 hover:text-surface-300 transition-colors"
        aria-label="Copy password"
      >
        <AnimatePresence mode="wait">
          {copied ? (
            <motion.span
              key="check"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
            >
              <Check className="h-3.5 w-3.5 text-brand-400" />
            </motion.span>
          ) : (
            <motion.span
              key="copy"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Copy className="h-3.5 w-3.5" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </span>
  );
}
